import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { useTenantId } from '@/hooks/useTenantId';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Settings as SettingsIcon, User, Building, Save, Mail, Users, HardHat } from 'lucide-react';
import { useEmailAccounts } from '@/hooks/useEmailAccounts';
import { EmailAccountCard } from '@/components/email/EmailAccountCard';
import { LabelPreferencesSettings } from '@/components/email/LabelPreferencesSettings';
import { NewsPreferencesSettings } from '@/components/settings/NewsPreferencesSettings';
import { NotificationPreferencesSettings } from '@/components/settings/NotificationPreferencesSettings';
import { PushNotificationSettings } from '@/components/settings/PushNotificationSettings';
import { PeriodicSyncSettings } from '@/components/settings/PeriodicSyncSettings';
import { UserManagement } from '@/components/settings/UserManagement';
import { PartnerManagementSettings } from '@/components/settings/PartnerManagementSettings';
import { useUserRole } from '@/hooks/useUserRole';

interface Profile {
  first_name: string;
  last_name: string;
  company_name: string;
  phone: string;
}

export default function Settings() {
  const { user } = useAuth();
  const { tenantId } = useTenantId();
  const { toast } = useToast();
  const { isTenantAdmin } = useUserRole();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<Profile>({
    first_name: '',
    last_name: '',
    company_name: '',
    phone: ''
  });

  const { 
    emailAccounts, 
    isLoading: emailLoading, 
    connectGmail,
    connectMicrosoft,
    syncAccount, 
    disconnectAccount 
  } = useEmailAccounts();

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, company_name, phone')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setProfile({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          company_name: data.company_name || '',
          phone: data.phone || ''
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user || !tenantId) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          tenant_id: tenantId,
          first_name: profile.first_name,
          last_name: profile.last_name,
          company_name: profile.company_name,
          phone: profile.phone
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile updated successfully"
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground">
              Manage your account and application preferences
            </p>
          </div>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail className="h-4 w-4 mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            {isTenantAdmin && (
              <TabsTrigger value="partners">
                <HardHat className="h-4 w-4 mr-2" />
                Partner Management
              </TabsTrigger>
            )}
            <TabsTrigger value="preferences">
              <SettingsIcon className="h-4 w-4 mr-2" />
              Preferences
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
              <CardDescription>
                Update your personal information and contact details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-sm text-muted-foreground">
                  Email address cannot be changed here. Contact support if needed.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company Name</Label>
                <Input
                  id="company"
                  value={profile.company_name}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  placeholder="ABC Construction"
                />
              </div>
              <Button onClick={handleSaveProfile} disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
          </TabsContent>

          <TabsContent value="email" className="space-y-6">

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Integration
              </CardTitle>
              <CardDescription>
                Connect your Gmail account to sync emails and enable communication features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {emailLoading ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Loading email accounts...</p>
                </div>
              ) : emailAccounts.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Email Accounts Connected</h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-4">
                    Connect your Gmail account to sync emails, track conversations, and communicate with contacts directly from the CRM.
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={() => connectGmail.mutate()}>
                      <Mail className="h-4 w-4 mr-2" />
                      Connect Gmail
                    </Button>
                    <Button onClick={() => connectMicrosoft.mutate()} variant="outline">
                      <Mail className="h-4 w-4 mr-2" />
                      Connect Microsoft 365
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {emailAccounts.map((account) => (
                      <EmailAccountCard
                        key={account.id}
                        account={account}
                        onSync={() => syncAccount.mutate(account.email_address)}
                        onDisconnect={() => disconnectAccount.mutate(account.id)}
                        isSyncing={syncAccount.isPending}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => connectGmail.mutate()}
                      className="flex-1"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Connect Gmail
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => connectMicrosoft.mutate()}
                      className="flex-1"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Connect Microsoft 365
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <UserManagement />
          </TabsContent>

          {isTenantAdmin && (
            <TabsContent value="partners" className="space-y-6">
              <PartnerManagementSettings />
            </TabsContent>
          )}

          <TabsContent value="preferences" className="space-y-6">

          <PushNotificationSettings />

          <PeriodicSyncSettings />

          <NewsPreferencesSettings />

          <NotificationPreferencesSettings />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}